import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
import { fetchOpenWorkOrderTargets } from "../../../../../../../../../../../../../api/kehrblatt/work-orders"
import {
  resolvePlanningBacklogList,
  type BacklogListSourceSystem,
  type BacklogListImportOptions,
} from "../../../../../../../../../../../../../api/kehrblatt/backlog-lists"
import { mergeProperties } from "../_shared/_logic/address-import"
import type {
  AddressCandidate,
  BacklogListCandidateResolution,
  BacklogListImport,
} from "../../../_shared/_logic/batch-upload-contract"
import { candidateFromProperty } from "../_shared/_logic/property-selection-selection"
import { backlogUnitIdsByPropertyId } from "../_shared/_logic/unit-selection"

const OPEN_TARGET_BATCH_SIZE = 100

const timingNow = () =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now()

const elapsedMs = (startedAt: number) => Math.round(timingNow() - startedAt)

const logBatchUploadTiming = (event: string, payload: Record<string, unknown>) => {
  console.info(event, payload)
}

export type ResolveBacklogListResult = {
  properties: Property[]
  candidates: AddressCandidate[]
  selectedPropertyIds: string[]
  selectedUnitIdsByPropertyId: Record<string, string[]>
  backlogListImport: BacklogListImport
}

const unresolvedBacklogCandidate = (
  row: BacklogListImport["rows"][number],
  status: BacklogListCandidateResolution["status"],
  sourceSystem: BacklogListSourceSystem,
): AddressCandidate => ({
  key: `backlog:${row.source_row_id}`,
  payload: {},
  propertyId: null,
  sources: ["backlog_pdf"],
  backlogResolution: {
    sourceRowId: row.source_row_id,
    sourceSystem,
    customerNumber: row.fields.customer_number,
    address: row.fields.address,
    matchReason: row.match_reason,
    status,
  },
})

const backlogCandidatesInSourceOrder = ({
  rows,
  properties,
  selectedPropertyIds,
  sourceSystem,
}: {
  rows: BacklogListImport["rows"]
  properties: Property[]
  selectedPropertyIds: string[]
  sourceSystem: BacklogListSourceSystem
}): AddressCandidate[] => {
  const propertyById = new Map(properties.map((property) => [property.id, property]))
  const selectedPropertyIdSet = new Set(selectedPropertyIds)
  const includedPropertyIds = new Set<string>()
  const candidates: AddressCandidate[] = []

  rows.forEach((row) => {
    const property = row.property_id ? propertyById.get(row.property_id) : null

    if (row.status === "matched" && property && selectedPropertyIdSet.has(property.id)) {
      if (!includedPropertyIds.has(property.id)) {
        candidates.push(candidateFromProperty(property, ["backlog_pdf"]))
        includedPropertyIds.add(property.id)
      }

      return
    }

    candidates.push(
      unresolvedBacklogCandidate(
        row,
        row.status === "ambiguous" ? "ambiguous" : "unmatched",
        sourceSystem,
      ),
    )
  })

  selectedPropertyIds.forEach((propertyId) => {
    const property = propertyById.get(propertyId)

    if (!property || includedPropertyIds.has(propertyId)) return

    candidates.push(candidateFromProperty(property, ["backlog_pdf"]))
  })

  return candidates
}

export const resolveBacklogList = async ({
  organizationId,
  seedProperties,
  sourceSystem,
  file,
  options,
  signal,
}: {
  organizationId: string
  seedProperties: Property[]
  sourceSystem?: BacklogListSourceSystem
  file: File
  options?: BacklogListImportOptions
  signal?: AbortSignal
}): Promise<ResolveBacklogListResult> => {
  const startedAt = timingNow()
  const response = await resolvePlanningBacklogList({
    organizationId,
    sourceSystem,
    file,
    options,
    signal,
  })

  if (signal?.aborted) throw new DOMException("Import abgebrochen", "AbortError")

  const propertyById = new Map(response.properties.map((property) => [property.id, property]))
  const selectedProperties = response.selected_property_ids
    .map((propertyId) => propertyById.get(propertyId))
    .filter((property): property is Property => Boolean(property))
  const selectedPropertyIds = selectedProperties.map((property) => property.id)
  const selectedUnitIdsByPropertyId = backlogUnitIdsByPropertyId({
    properties: selectedProperties,
    rows: response.rows,
    sourceSystem: response.source_system,
  })
  const openCount = response.summary.unmatched_count + response.summary.ambiguous_count
  const openEntryLabel = openCount === 1 ? "Eintrag" : "Einträge"
  const candidates = backlogCandidatesInSourceOrder({
    rows: response.rows,
    properties: response.properties,
    selectedPropertyIds,
    sourceSystem: response.source_system,
  })

  logBatchUploadTiming("batch_backlog_list_resolve_completed", {
    parsed_count: response.summary.parsed_count,
    matched_count: response.summary.matched_count,
    unmatched_count: response.summary.unmatched_count,
    ambiguous_count: response.summary.ambiguous_count,
    selected_property_count: selectedPropertyIds.length,
    duration_ms: elapsedMs(startedAt),
  })

  newNotification({
    class: openCount ? "warning" : "success",
    html: !selectedPropertyIds.length
      ? `Rückstandsliste geladen. Keine Liegenschaft erkannt, ${openCount} ${openEntryLabel} offen.`
      : openCount
        ? `Rückstandsliste geladen. ${selectedPropertyIds.length} Liegenschaften erkannt, ${openCount} ${openEntryLabel} offen.`
        : `Rückstandsliste geladen. ${selectedPropertyIds.length} Liegenschaften erkannt.`,
  })

  return {
    properties: mergeProperties(seedProperties, response.properties),
    candidates,
    selectedPropertyIds,
    selectedUnitIdsByPropertyId,
    backlogListImport: {
      sourceSystem: response.source_system,
      options: response.import_options ?? options,
      importId: crypto.randomUUID(),
      fileName: file.name,
      sourceColumns: response.source_columns ?? [],
      rows: response.rows,
      summary: response.summary,
    },
  }
}

export const loadOpenWorkOrderTargets = async ({
  organizationId,
  propertyIds,
}: {
  organizationId: string
  propertyIds: string[]
}) => {
  const startedAt = timingNow()
  const orders: Awaited<ReturnType<typeof fetchOpenWorkOrderTargets>> = []
  // All rows are loaded immediately; keep the GET query below URL-size limits.
  for (let offset = 0; offset < propertyIds.length; offset += OPEN_TARGET_BATCH_SIZE) {
    const targets = await fetchOpenWorkOrderTargets({
      organizationId,
      propertyIds: propertyIds.slice(offset, offset + OPEN_TARGET_BATCH_SIZE),
      requiredAccess: "manage",
    })
    orders.push(...targets)
  }
  logBatchUploadTiming("batch_open_work_order_targets_loaded", {
    property_count: propertyIds.length,
    open_target_count: orders.length,
    duration_ms: elapsedMs(startedAt),
  })

  return orders
}
